import { ConductorOneChildActive } from "@frui.ts/screens";
import type { ScreenBase } from "@frui.ts/screens";
import type { interfaces } from "inversify";
import { action, computed } from "mobx";
import LocalizationService from "services/localizationService";
import { Modules } from "config";
import { formatLicensePlate } from "utils/localizations";
import type VehicleRegisterVehicle from "entities/vehicleRegisterVehicleResponse";
import VehicleRegisterDetailInfoViewModel from "./vehicleRegisterDetailInfoViewModel";
import VehicleRegisterHistoriesViewModel from "./vehicleRegisterHistoriesViewModel";
import type Vehicle from "entities/vehicleResponse";
import EventBus, { vehicleSynchronizationOpenSyncRSV } from "services/eventBus";
import SecurityService from "services/securityService";

export default class VehicleRegisterDetailViewModel extends ConductorOneChildActive<ScreenBase> {
  private readonly chargeDetailInfoViewModel: VehicleRegisterDetailInfoViewModel;
  private readonly chargeHistoriesViewModel: VehicleRegisterHistoriesViewModel;

  constructor(
    private vehicleRegister: VehicleRegisterVehicle,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    private localizationService: LocalizationService,
    vehicleRegisterDetailInfoViewModelFactory: ReturnType<typeof VehicleRegisterDetailInfoViewModel.Factory>,
    vehicleRegisterHistoriesViewModelFactory: ReturnType<typeof VehicleRegisterHistoriesViewModel.Factory>
  ) {
    super();
    this.chargeDetailInfoViewModel = vehicleRegisterDetailInfoViewModelFactory(this.vehicleRegister);
    this.chargeHistoriesViewModel = vehicleRegisterHistoriesViewModelFactory(this.vehicleRegister);
    this.children.push(this.chargeDetailInfoViewModel, this.chargeHistoriesViewModel);
    this.navigationName = "";
  }

  protected onInitialize(): Promise<any> | void {
    this.tryActivateChild(this.children[0]);
    return super.onInitialize();
  }

  @computed
  get name() {
    return this.localizationService.translateModule(Modules.callCenter, "vehicleRegisterDetail.title");
  }

  translate(code: string) {
    return this.localizationService.translateModule(Modules.callCenter, `vehicleRegisterDetail.${code.toString()}`);
  }

  @action.bound
  allowToSync() {
    return this.securityService.allowTo("callcenter", "write");
  }

  @computed
  get licensePlate() {
    const vehicle: Vehicle = {
      licensePlate: this.vehicleRegister.licensePlate,
      countryId: this.vehicleRegister.countryId,
    };
    return this.localizationService
      .translateModule(Modules.callCenter, "vehicleRegisterDetail.licensePlate")
      .replace("%licensePlate%", formatLicensePlate(vehicle));
  }

  @action.bound
  onClose() {
    this.requestClose();
  }

  @action.bound
  onVehicleSynchronization() {
    this.eventBus.publish(vehicleSynchronizationOpenSyncRSV({ openSync: true, vehicleRegister: this.vehicleRegister }));
  }

  static Factory({ container }: interfaces.Context) {
    return (vehicleRegister: VehicleRegisterVehicle) =>
      new VehicleRegisterDetailViewModel(
        vehicleRegister,
        container.get(EventBus),
        container.get(SecurityService),
        container.get(LocalizationService),
        container.get(VehicleRegisterDetailInfoViewModel.Factory),
        container.get(VehicleRegisterHistoriesViewModel.Factory)
      );
  }
}
